import { TZDate } from "@date-fns/tz";
import { formatInTimeZone as formatInTimeZoneOriginal } from "date-fns-tz";
import {
  parseISO,
  format,
  formatDistanceToNow,
  startOfDay,
  endOfDay,
  isValid,
} from "date-fns";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import { getDateLocale } from "@shared/utils/dates/locale";
import { normalizeTimezone } from "@shared/utils/dates/timezone/normalizeTimezone";
import { getLocaleForTimezone } from "@shared/utils/locale";

/**
 * Supported date input for boundary and formatting functions (string | number | Date).
 * Callers can pass an ISO string from the API, a Date, or a timestamp.
 */
export type DateInput = string | number | Date;
type ISODateInput = Date | TZDate | string | number | null | undefined;

/**
 * Converts Moment.js format strings to date-fns format strings.
 * Supports common patterns used in the codebase.
 */
const convertMomentFormatToDateFns = (momentFormat: string): string => {
  return (
    momentFormat
      // Escape characters - MUST BE FIRST
      // Moment.js uses [text] to escape, date-fns uses 'text'
      .replace(/\[([^\]]*)\]/g, "'$1'")
      // Year - must be replaced before other tokens
      .replace(/YYYY/g, "yyyy")
      .replace(/YY/g, "yy")
      // Day - must be replaced before weekday tokens
      .replace(/DD/g, "dd")
      .replace(/Do/g, "do") // Ordinal day (1st, 2nd, 3rd)
      .replace(/D/g, "d")
      // Weekday - must come after day replacements
      .replace(/dddd/g, "EEEE") // Full weekday name
      .replace(/ddd/g, "EEE") // Short weekday name (Mon, Tue, Wed)
      // AM/PM - Both A and a become 'a' in date-fns
      .replace(/A/g, "a")
      // Timezone abbreviation - z in moment.js = timezone abbr (CEST, PST)
      // In date-fns, z = GMT offset, zzz = custom timezone abbr via getTimezoneAbbreviation
      .replace(/z+/g, (match) => (match.length === 1 ? "zzz" : match))
      // Timezone offset
      .replace(/ZZ/g, "XX") // Offset without colon (+0800)
      .replace(/Z/g, "XXX") // Offset with colon (+08:00)
    // Note: Month (MMMM, MMM, MM, M), Hour (HH, H, hh, h),
    // Minute/Second (mm, m, ss, s) work the same in both libraries
  );
};

/**
 * Detects if a format string contains Moment.js-specific tokens
 * that need conversion to date-fns format.
 *
 * @param formatString - Format string to check
 * @returns true if format contains Moment.js tokens, false otherwise
 *
 * @example
 * isMomentFormat("YYYY-MM-DD"); // true (YYYY is Moment)
 * isMomentFormat("yyyy-MM-dd"); // false (yyyy is date-fns)
 * isMomentFormat("ddd MMM D, YYYY | h:mm A"); // true (D, YYYY, A are Moment)
 * isMomentFormat("HH:mm[h]"); // true ([h] is Moment escape syntax)
 */
export const isMomentFormat = (formatString: string): boolean => {
  // Moment.js-specific tokens that differ from date-fns
  const momentTokens = [
    /YYYY/, // year (yyyy in date-fns)
    /YY(?!YY)/, // 2-digit year (yy in date-fns)
    /DD/, // day of month with zero padding (dd in date-fns)
    /D(?!D)/, // day of month without zero padding (d in date-fns)
    /Do/, // ordinal day (do in date-fns)
    /dddd/, // full weekday (EEEE in date-fns)
    /ddd(?!d)/, // short weekday (EEE in date-fns)
    /A/, // AM/PM uppercase (a in date-fns)
    /ZZ(?!Z)/, // timezone offset without colon (XX in date-fns)
    /(?:^|[^z])z(?![z])/, // timezone abbreviation (z in Moment -> zzz in date-fns)
    /\[.*?\]/, // Moment.js escape characters [text] (date-fns uses 'text')
  ];
  return momentTokens.some((token) => token.test(formatString));
};

/**
 * Detects if a format string is already in date-fns format.
 *
 * @param formatString - Format string to check
 * @returns true if format contains date-fns tokens, false otherwise
 *
 * @example
 * isDateFnsFormat("yyyy-MM-dd"); // true (yyyy is date-fns)
 * isDateFnsFormat("YYYY-MM-DD"); // false (YYYY is Moment)
 * isDateFnsFormat("EEEE, MMMM do yyyy"); // true (EEEE, do are date-fns)
 */
export const isDateFnsFormat = (formatString: string): boolean => {
  // If format uses Moment-specific tokens, it's not date-fns (M/MM are shared; YYYY/DD etc. are Moment)
  if (isMomentFormat(formatString)) return false;
  // date-fns-specific tokens (date + time)
  const dateFnsTokens = [
    /yyyy/, // year
    /yy(?!yy)/, // 2-digit year
    /M(?!M)/, // month 1-12 (single M; avoid matching MM, MMM, MMMM)
    /MM(?!M)/, // month 01-12
    /MMM(?!M)/, // short month name (Jan, Feb)
    /MMMM/, // full month name
    /d(?!d)/, // day of month (single d; avoid matching dd, ddd, dddd)
    /dd/, // day of month padded
    /do/, // ordinal day
    /EEEE/, // full weekday
    /EEE(?!E)/, // short weekday
    /XXX/, // timezone offset with colon
    /XX(?!X)/, // timezone offset without colon
    /zzz/, // timezone abbreviation (e.g., "EST", "PST")
    /\ba\b/, // am/pm (date-fns uses 'a'; Moment uses 'A' for uppercase)
  ];
  return dateFnsTokens.some((token) => token.test(formatString));
};

/**
 * Wrapper around date-fns-tz's formatInTimeZone that automatically applies
 * the current locale from the NEXT_LOCALE cookie.
 *
 * This ensures all date formatting respects the user's selected language
 * without needing to pass locale explicitly throughout the codebase.
 *
 * @param date - Date to format (Date, number, or string)
 * @param timeZone - IANA timezone identifier (e.g., "America/Edmonton")
 * @param formatStr - date-fns format string (e.g., "yyyy-MM-dd")
 * @param options - Optional format options (locale will be overridden)
 * @returns Formatted date string in the user's selected locale
 *
 * @example
 * // With English locale (NEXT_LOCALE=en)
 * formatInTimeZone(date, "America/Edmonton", "EEEE MMMM do, yyyy");
 * // → "Tuesday December 29th, 2026"
 *
 * // With French locale (NEXT_LOCALE=fr)
 * formatInTimeZone(date, "America/Edmonton", "EEEE MMMM do, yyyy");
 * // → "mardi décembre 29, 2026"
 */
export const formatInTimeZone = (
  date: Date | number | string,
  timeZone: string,
  formatStr: string,
  options?: Parameters<typeof formatInTimeZoneOriginal>[3]
): string => {
  const locale = getDateLocale();
  const resolvedTimeZone = normalizeTimezone(timeZone) ?? timeZone;
  return formatInTimeZoneOriginal(date, resolvedTimeZone, formatStr, {
    locale,
    ...options,
  });
};

/**
 * Gets timezone abbreviation (like "PST", "AEDT", "IST") using Intl.DateTimeFormat
 * with smart locale selection based on timezone region.
 *
 * @param localeOverride - When provided, overrides the geography-based locale.
 *   Use this to get language-appropriate abbreviations (e.g., "fr-CA" produces
 *   "HNR" instead of "MST" for America/Edmonton).
 */
export const getTimezoneAbbreviation = (
  date: Date | string,
  timezone: string,
  localeOverride?: string
): string => {
  const resolvedTimezone = normalizeTimezone(timezone) ?? timezone;
  try {
    const locale = localeOverride ?? getLocaleForTimezone(resolvedTimezone);
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone: resolvedTimezone,
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(new Date(date));
    return (
      parts.find((part) => part.type === "timeZoneName")?.value ||
      resolvedTimezone
    );
  } catch {
    return resolvedTimezone;
  }
};

/**
 * Formats a standard date (string | Date | undefined) using date-fns.
 * Replaces the old getMomentFormattedDate function.
 */
const formatStandardDate = ({
  date,
  timezone,
  format: formatStr,
}: {
  date: string | Date | undefined;
  timezone: string | null;
  format: string;
}): string => {
  // Convert to Date object
  let dateObj: Date;
  if (!date) {
    dateObj = new Date();
  } else if (typeof date === "string") {
    dateObj = parseISO(date);
  } else if (
    date &&
    typeof date === "object" &&
    "toDate" in date &&
    typeof (date as { toDate: () => Date }).toDate === "function"
  ) {
    // Handle Moment objects or other objects with toDate() method
    dateObj = (date as { toDate: () => Date }).toDate();
  } else {
    dateObj = date as Date;
  }

  // Validate the date
  if (!isValid(dateObj)) return "";

  // Resolve timezone - use provided timezone, or browser timezone if null
  const resolvedTimezone =
    timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Check if format includes timezone abbreviation token (zzz)
  const hasTimezone = formatStr.includes("zzz");

  if (hasTimezone) {
    const tzAbbr = getTimezoneAbbreviation(dateObj, resolvedTimezone);
    const wasWrapped = /\(\s*zzz\s*\)/.test(formatStr);
    const formatWithoutTz = formatStr
      .replace(/\s*\(\s*zzz\s*\)\s*/g, " ")
      .replace(/\s*zzz\s*/g, " ")
      .trim();
    if (!formatWithoutTz) {
      return wasWrapped ? `(${tzAbbr})` : tzAbbr;
    }
    const formattedWithoutTz = formatInTimeZone(
      dateObj,
      resolvedTimezone,
      formatWithoutTz
    );
    const formattedTz = wasWrapped ? `(${tzAbbr})` : tzAbbr;
    return `${formattedWithoutTz} ${formattedTz}`.trim();
  }

  // No timezone in format - use standard formatting
  return formatInTimeZone(dateObj, resolvedTimezone, formatStr);
};

/**
 * Formats a TZDate using date-fns with timezone support.
 * Keeps the original simple implementation for TZDate only.
 */
const getDateFnsFormattedDate = ({
  date,
  timezone,
  format: formatStr,
}: {
  date: TZDate;
  timezone: string | null;
  format: string;
}): string => {
  const resolvedTimezone =
    timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Check if format includes timezone abbreviation token (zzz)
  const hasTimezone = formatStr.includes("zzz");

  if (hasTimezone) {
    const dateForAbbr = new Date(date.toISOString());
    const tzAbbr = getTimezoneAbbreviation(dateForAbbr, resolvedTimezone);
    const wasWrapped = /\(\s*zzz\s*\)/.test(formatStr);
    const formatWithoutTz = formatStr
      .replace(/\s*\(\s*zzz\s*\)\s*/g, " ")
      .replace(/\s*zzz\s*/g, " ")
      .trim();
    if (!formatWithoutTz) {
      return wasWrapped ? `(${tzAbbr})` : tzAbbr;
    }
    const formattedWithoutTz = formatInTimeZone(
      date,
      resolvedTimezone,
      formatWithoutTz
    );
    const formattedTz = wasWrapped ? `(${tzAbbr})` : tzAbbr;
    return `${formattedWithoutTz} ${formattedTz}`.trim();
  }

  // No timezone in format - use standard formatting
  return formatInTimeZone(date, resolvedTimezone, formatStr);
};

/**
 * Returns a formatted date string based on our common usage
 * of displaying event and ticket type dates.
 * Can be used to display any other date.
 *
 * Supports both Moment.js and date-fns format strings.
 * Automatically detects the format type and converts Moment.js formats to date-fns.
 * Renamed from getFormattedDate (format* → display string).
 */
export const formatDate = (
  options: {
    date?: string | Date | TZDate;
    timezone: string | null;
    format?: string;
    displayTime?: boolean;
    timeFormat?: string;
    displayTimezone?: boolean;
  } = { timezone: null } // Explicitly use browser timezone if timezone not provided (i.e null)
): string => {
  const {
    date,
    timezone,
    format: formatOpt,
    displayTime,
    timeFormat,
    displayTimezone,
  } = options;
  const defaultFormat = "MMMM d, yyyy";
  const defaultTimeFormat = "h:mm a";
  let dateFormat = formatOpt || defaultFormat;

  // Automatically detect and convert moment format strings
  if (formatOpt) {
    if (isMomentFormat(formatOpt)) {
      dateFormat = convertMomentFormatToDateFns(formatOpt);
    } else if (!isDateFnsFormat(formatOpt) && formatOpt !== defaultFormat) {
      // Format doesn't match either system - log warning
      recordAppError(
        `[formatDate] Format string "${formatOpt}" doesn't match Moment.js or date-fns patterns. Using as-is.`
      );
    }
  }

  // Add time format
  if (displayTime) {
    let convertedTimeFormat = defaultTimeFormat;
    if (timeFormat) {
      convertedTimeFormat = isMomentFormat(timeFormat)
        ? convertMomentFormatToDateFns(timeFormat)
        : timeFormat;
    }
    dateFormat = `${dateFormat} ${convertedTimeFormat}`;
  }

  // Usually will want to display the timezone if we are displaying the time
  const shouldDisplayTimezone = displayTimezone ?? displayTime;
  if (shouldDisplayTimezone) {
    dateFormat = `${dateFormat} zzz`;
  }

  // Route to appropriate formatter based on date type
  const isTZDate = date instanceof TZDate;
  if (isTZDate) {
    return getDateFnsFormattedDate({ date, timezone, format: dateFormat });
  }
  return formatStandardDate({ date, timezone, format: dateFormat });
};

/**
 * Returns a formatted time string (e.g. "9:00 AM").
 * Renamed from getFormattedTime.
 */
export const formatTime = (options: {
  date: Date | string;
  timezone: string | null;
  format?: string;
  showTimezone?: boolean;
}): string => {
  const { date, timezone, format: formatOpt, showTimezone } = options;
  const defaultTimeFormat = "h:mm a";
  let timeFormat = formatOpt ?? defaultTimeFormat;
  if (isMomentFormat(timeFormat)) {
    timeFormat = convertMomentFormatToDateFns(timeFormat);
  }
  if (showTimezone) {
    timeFormat = `${timeFormat} zzz`;
  }
  return formatStandardDate({ date, timezone, format: timeFormat });
};

/**
 * Formats a start and end instant as a single line (e.g. "9:00 AM - 11:00 PM MST").
 * End time includes the timezone abbreviation; start does not (matches common event copy).
 */
export const formatTimeRange = (options: {
  start: Date | string;
  end: Date | string;
  timezone: string | null;
}): string => {
  const { start, end, timezone } = options;
  const startStr = formatTime({ date: start, timezone });
  const endStr = formatTime({
    date: end,
    timezone,
    showTimezone: true,
  });
  if (!startStr || !endStr) {
    return "";
  }
  return `${startStr} - ${endStr}`;
};

/**
 * Takes a UTC datetime string, converts it to the specified timezone,
 * and returns the time formatted in 12-hour format (e.g., "11:30 AM").
 * Renamed from getDisplayTimeFromUTC.
 *
 * @param utcDateString - The date/time in UTC format.
 * @param timezone - The venue timezone (e.g., "America/New_York").
 * @returns The time formatted as "h:mm a" (e.g., "12:00 AM").
 */
export const formatTimeFromUTC = (
  utcDateString: string | undefined,
  timezone: string
): string => {
  if (!utcDateString) return "12:00 AM";

  // Parse the UTC date string
  const utcDate = parseISO(utcDateString);

  // Format as 12-hour time
  return formatInTimeZone(utcDate, timezone, "h:mm a");
};

/**
 * Returns relative time string (e.g. "2 hours ago"). Uses current date locale.
 */
export const formatRelativeTime = (
  date: DateInput,
  options: { addSuffix?: boolean } = { addSuffix: true }
): string => {
  const d = date instanceof Date ? date : new Date(date);
  if (!isValid(d)) return "";
  const locale = getDateLocale();
  return formatDistanceToNow(d, {
    addSuffix: options.addSuffix ?? true,
    locale,
  });
};

/**
 * Formats a date-like value into a YYYY-MM-DD string that matches HTML date inputs.
 * Returns an empty string when the value cannot be converted into a valid date.
 */
export const formatDateToISODateString = (date?: ISODateInput): string => {
  if (!date) return "";
  const dateValue =
    date instanceof Date ? date : new Date(date as string | number);
  if (!isValid(dateValue)) return "";
  return format(dateValue, "yyyy-MM-dd");
};

/**
 * Formats a date as the start of that day (00:00:00.000) in ISO 8601.
 * Useful for API date range params (e.g. start_date).
 */
export const formatStartOfDayToISO = (date: Date): string =>
  startOfDay(date).toISOString();

/**
 * Formats a date as the end of that day (23:59:59.999) in ISO 8601.
 * Useful for API date range params (e.g. end_date).
 */
export const formatEndOfDayToISO = (date: Date): string =>
  endOfDay(date).toISOString();
